/**
 * Die Auftragsliste — `PORTAL-002`.
 *
 * Der Filter ist bewusst der Status und nicht der Kunde: Wer im Büro sitzt,
 * fragt „was läuft gerade" und „was ist abgeschlossen", nicht „zeig mir alles
 * von Hufnagel" — dafür geht man in die Kundenakte. Ein Filter, der die
 * falsche Frage beantwortet, wird zur Zierleiste über einer Liste, die man
 * doch von Hand durchsieht.
 */

import { useMemo, useState } from "react";

import type { Auftragsstatus } from "@hwc/demo-data";
import { einsaetzeZuAuftrag, findeKunde, findePerson } from "@hwc/demo-data";

import { useSitzung } from "../../src/daten/Sitzung.js";
import {
  Einsatzabzeichen,
  Leer,
  Liste,
  Zeile,
  datum,
  zumAuftrag,
} from "../../src/bereiche/bausteine.js";

type Filter = "laufend" | "geplant" | "abgeschlossen" | "alle";

const FILTER: readonly { readonly wert: Filter; readonly text: string }[] = [
  { wert: "laufend", text: "Laufend" },
  { wert: "geplant", text: "Geplant" },
  { wert: "abgeschlossen", text: "Abgeschlossen" },
  { wert: "alle", text: "Alle" },
];

function passt(status: Auftragsstatus, filter: Filter): boolean {
  if (filter === "alle") return true;
  if (filter === "laufend") return status === "in-arbeit" || status === "zugewiesen";
  if (filter === "geplant") return status === "neu" || status === "geplant";
  return status === "abgeschlossen" || status === "geprueft" || status === "storniert";
}

export function Auftragsliste() {
  const { bestand } = useSitzung();
  const [filter, setzeFilter] = useState<Filter>("laufend");
  const [frage, setzeFrage] = useState("");

  const auftraege = useMemo(() => {
    const q = frage.trim().toLowerCase();
    return bestand.auftraege
      .filter((a) => passt(a.status, filter))
      .filter((a) => {
        if (!q) return true;
        const kunde = findeKunde(bestand, a.kunde);
        return (
          a.titel.toLowerCase().includes(q)
          || a.nummer.includes(q)
          || (kunde?.name.toLowerCase().includes(q) ?? false)
        );
      })
      .slice()
      // Neueste zuerst: Die Auftragsnummer ist aufsteigend vergeben, und was
      // gerade angelegt wurde, ist das, worüber gerade gesprochen wird.
      .sort((x, y) => y.nummer.localeCompare(x.nummer));
  }, [bestand, filter, frage]);

  return (
    <>
      <div className="bereichskopf">
        <h1>Aufträge</h1>
        <span className="bereichskopf__zusatz">
          {auftraege.length} von {bestand.auftraege.length}
        </span>
      </div>

      <div className="filter">
        {FILTER.map((f) => (
          <button
            key={f.wert}
            type="button"
            className={`filter__knopf${filter === f.wert ? " filter__knopf--aktiv" : ""}`}
            onClick={() => setzeFilter(f.wert)}
            aria-pressed={filter === f.wert}
          >
            {f.text}
          </button>
        ))}
      </div>

      <input
        className="suchfeld"
        type="search"
        placeholder="Auftragsnummer, Titel oder Kunde"
        aria-label="Aufträge durchsuchen"
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
      />

      {auftraege.length === 0 ? (
        <Leer
          text={
            frage
              ? `Kein Auftrag passt zu „${frage}".`
              : "In diesem Zustand gibt es gerade keinen Auftrag."
          }
        />
      ) : (
        <Liste>
          {auftraege.map((a) => {
            const kunde = findeKunde(bestand, a.kunde);
            const einsaetze = einsaetzeZuAuftrag(bestand, a.ref);
            // Der Zustand, der das Büro interessiert, ist der des laufenden
            // Einsatzes — nicht der Sammelstatus des Auftrags. Bei mehreren
            // gewinnt der, der am weitesten vorn im Ablauf steht.
            const laufend = einsaetze.find(
              (e) => e.zustand !== "geprueft" && e.zustand !== "storniert",
            );
            return (
              <Zeile
                key={a.ref.opaqueId}
                zu={zumAuftrag(a.nummer)}
                titel={`${a.nummer} — ${a.titel}`}
                unterzeile={
                  <>
                    {kunde?.name ?? "unbekannter Kunde"}
                    {a.gewerk ? ` · ${a.gewerk}` : ""} ·{" "}
                    {findePerson(bestand, a.verantwortlich)?.name ?? ""}
                  </>
                }
                abzeichen={laufend ? <Einsatzabzeichen zustand={laufend.zustand} /> : null}
                rechts={
                  <>
                    {einsaetze.length} {einsaetze.length === 1 ? "Einsatz" : "Einsätze"}
                    <br />
                    seit {datum(a.angelegt)}
                  </>
                }
              />
            );
          })}
        </Liste>
      )}
    </>
  );
}
